import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * `system` is a real third state, not a synonym for whichever theme is
 * showing: it keeps following the operating system when that changes, which
 * an explicit `light` or `dark` deliberately stops doing.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

/** What is actually on screen once `system` has been resolved. */
export type ResolvedTheme = 'light' | 'dark';

/**
 * Shared with the pre-paint script in `index.html`. That script runs before
 * React and applies the same class from the same key, so the first frame is
 * already the right theme. Change one, change both.
 */
export const THEME_STORAGE_KEY = 'steado-admin-theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Storage can throw outright, not merely return null: Chrome denies access to
 * `localStorage` when third-party cookies are blocked and the page is framed,
 * and Safari's private mode has historically thrown on write. A console that
 * refuses to render because it could not read a colour preference would be a
 * poor trade.
 */
export function readStoredPreference(): ThemePreference {
  try {
    const stored: unknown = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

function storePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Preference is lost on reload, which beats failing the interaction.
  }
}

function systemPrefersDark(): boolean {
  return window.matchMedia(DARK_QUERY).matches;
}

interface ThemeContextValue {
  readonly preference: ThemePreference;
  readonly resolved: ResolvedTheme;
  readonly setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { readonly children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [systemIsDark, setSystemIsDark] = useState(systemPrefersDark);

  // Subscribed once rather than per preference change: the OS can flip while
  // the console is pinned to an explicit theme, and this way `resolved` is
  // already right the moment someone switches back to `system`.
  useEffect(() => {
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      setSystemIsDark(event.matches);
    };

    query.addEventListener('change', onChange);
    return () => {
      query.removeEventListener('change', onChange);
    };
  }, []);

  // Derived, not stored: a second copy of this in state would need an effect
  // to keep it in step, and that effect is the cascading-render bug.
  const resolved: ResolvedTheme = preference === 'system' ? (systemIsDark ? 'dark' : 'light') : preference;

  // The class goes on `<html>` rather than a wrapper element because Radix
  // portals its overlays to `document.body`; a wrapper would leave every
  // dialog, drawer and select menu on the light palette.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    storePreference(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);

  if (value === null) {
    throw new Error('useTheme must be used inside <ThemeProvider>.');
  }

  return value;
}
